import { useTranslation } from 'react-i18next';
import { Clock, MapPin } from 'lucide-react';
import Badge from '@/components/Shared/Badge';
import { TaskChecklistPanel } from '@/components/Dashboard/Employee/TaskChecklistPanel';
import type { TaskChecklistGroup } from '@/store/useMyChecklistStore';

interface ChecklistTaskCardProps {
  /** The planned day the confirmations are recorded for — not necessarily today. */
  date: string;
  task: TaskChecklistGroup;
  readOnly?: boolean;
}

/**
 * One task with its checklist. Shared by the day view and the "open from previous days"
 * section so a leftover item is ticked off exactly the way today's is.
 */
export function ChecklistTaskCard({ date, task, readOnly = false }: ChecklistTaskCardProps) {
  const { t } = useTranslation('myChecklists');
  const openCount = (task.items ?? []).filter((i) => !i.isCompleted).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-slate-900 truncate">{task.objectName}</div>
          <div className="flex items-center gap-1 text-[12px] text-slate-500 truncate">
            <MapPin className="w-3 h-3 shrink-0" aria-hidden />
            <span className="truncate">{task.taskName}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {task.startTime && (
            <span className="inline-flex items-center gap-1 text-[12px] text-slate-500 tabular-nums">
              <Clock className="w-3 h-3" aria-hidden />
              {task.startTime.substring(0, 5)}
            </span>
          )}
          <Badge variant={openCount > 0 ? 'warning' : 'success'}>
            {openCount > 0 ? t('summary.open', { count: openCount }) : t('summary.allDone')}
          </Badge>
        </div>
      </div>

      <TaskChecklistPanel date={date} group={task} readOnly={readOnly} />
    </div>
  );
}

export default ChecklistTaskCard;
